/**
 * BrainCompletionHandoff — the "never a dead end" screen.
 *
 * After Business Brain approval: hands off to Brand Brain.
 * After Brand Brain approval: hands off to First Campaign generation.
 * The founder never lands on a passive "You're done" state.
 */
import { useNavigate } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";

import type { BrainMissionType } from "@/entities/brain-missions";

interface BrainCompletionHandoffProps {
  type: BrainMissionType;
}

export function BrainCompletionHandoff({ type }: BrainCompletionHandoffProps) {
  const navigate = useNavigate();
  const isBusiness = type === "business_brain";
  const headline = isBusiness
    ? "Akinex understands your business."
    : "Your brand voice is ready.";
  const subheading = isBusiness
    ? "Now let's teach Akinex how your brand should sound when it speaks for you."
    : "The Business Brain is approved, the Brand Brain is approved. Time to build your first campaign.";
  const nextLabel = isBusiness ? "Continue to Brand Brain →" : "Generate Your First Campaign →";
  const nextRoute = isBusiness ? "/brand-brain" : "/campaigns/new";
  const nextTestId = isBusiness
    ? "brain-handoff-continue-brand"
    : "brain-handoff-generate-campaign";

  return (
    <div
      className="mx-auto flex max-w-2xl flex-col items-start gap-8 px-2 py-8 text-white"
      data-testid={`brain-completion-handoff-${type}`}
    >
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-cyan-400/15 text-cyan-300">
          <CheckCircle2 className="h-5 w-5" />
        </div>
        <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-cyan-300/80">
          {isBusiness ? "Business Brain approved" : "Brand Brain approved"}
        </div>
      </div>

      <h1
        className="text-3xl font-semibold leading-tight tracking-tight sm:text-4xl"
        data-testid="brain-handoff-headline"
      >
        {headline}
      </h1>
      <p className="max-w-xl text-base leading-relaxed text-white/70 sm:text-lg">
        {subheading}
      </p>

      <button
        type="button"
        onClick={() => navigate(nextRoute)}
        className="rounded-full bg-gradient-to-r from-cyan-400 via-sky-500 to-violet-500 px-6 py-3 text-sm font-semibold text-slate-950 shadow-[0_10px_30px_-10px_rgba(0,240,255,0.5)] hover:brightness-110 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white transition"
        data-testid={nextTestId}
      >
        {nextLabel}
      </button>

      {isBusiness && (
        <div className="text-xs text-white/50">
          You can revisit and refine any answer later — everything you teach Akinex
          keeps improving over time.
        </div>
      )}
    </div>
  );
}
